import { Alert } from '@ksyos/design-system';
import { type JSX, useEffect } from 'react';
import type { HolterProcessData } from '@common/interfaces/cardiology/holter.types';
import type { ContentComponentsMap, InputComponentsMap } from '@frontend/formEngine/editForm/editForm.types';
import {
    type HolterGradingContentKeys,
    type HolterGradingData,
    holterGradingSections,
} from '@common/validation/customControls/cardiology/holter/holterGradingSchemas';
import { holterGradingRelatieEcgEnKlachtenOptions } from '@common/validation/customControls/cardiology/holter/utils/gradingAndConsultUtils';
import { HolterAdviceQuestionReadOnly } from '@frontend/components/pages/episodeCategory/customControls/cardiology/holter/holterGrading/HolterAdviceQuestionReadOnly';
import { HolterGradingAdviceSelect } from '@frontend/components/pages/episodeCategory/customControls/cardiology/holter/holterGrading/HolterGradingAdviceSelect';
import { HolterGradingUrgencySelect } from '@frontend/components/pages/episodeCategory/customControls/cardiology/holter/holterGrading/HolterGradingUrgencySelect';
import { TextInputTypeAhead } from '@frontend/components/pages/episodeCategory/customControls/TextInputTypeAhead';
import { EditForm } from '@frontend/formEngine/editForm/EditForm';
import { useProcessActivityEditContext } from '@frontend/utils';

export function HolterGradingEdit(): JSX.Element {
    const { data, errors, onChange, processActivityContextData } = useProcessActivityEditContext<
        HolterGradingData & {
            processData: HolterProcessData;
        }
    >();
    const adviceQuestion = processActivityContextData?.processData.adviceQuestion;

    useEffect(() => {
        onChange({ adviceQuestion: adviceQuestion }, 'adviceQuestion');
    }, []);

    return (
        <EditForm
            contentComponents={getHolterGradingContentComponents()}
            inputComponents={getInputComponents()}
            sections={holterGradingSections}
        />
    );

    function getInputComponents(): InputComponentsMap<keyof HolterGradingData> {
        const map: InputComponentsMap<keyof HolterGradingData> = new Map();

        map.set('advice', <HolterGradingAdviceSelect />);

        map.set('urgency', <HolterGradingUrgencySelect />);

        map.set(
            'relationEcgAndComplaints',
            <TextInputTypeAhead
                error={errors.get('relationEcgAndComplaints')}
                label="Relatie ECG en klachten"
                onChange={(relationEcgAndComplaints) =>
                    onChange({ relationEcgAndComplaints: relationEcgAndComplaints }, 'relationEcgAndComplaints')
                }
                options={holterGradingRelatieEcgEnKlachtenOptions}
                value={data.relationEcgAndComplaints}
                variant="outline"
            />
        );

        map.set('adviceQuestion', <HolterAdviceQuestionReadOnly adviceQuestion={adviceQuestion ?? undefined} />);

        return map;
    }

    function getHolterGradingContentComponents(): ContentComponentsMap<HolterGradingContentKeys> {
        const map: ContentComponentsMap<HolterGradingContentKeys> = new Map();
        map.set(
            'adviceQuestionAlert',
            <Alert
                appearance="info"
                hasIcon={true}
            >
                De huisarts heeft aangegeven dat aanvullend advies gewenst is bij een conclusie Afwijkend. Daarom wordt
                deze beoordeling uitgebreid met een consult.
            </Alert>
        );

        return map;
    }
}
